/**
 * Schema forms (moved from the workbench start drawer by 21d). Renders controls from a **served** JSON Schema
 * (draft 2020-12, as `z.toJSONSchema` emits it): a capability's `inputSchema`, a process's start `inputSchema`
 * (`GET /processes/{name}`) or a work item's `outputSchema` (`GET /work-items/{id}`). Callers never bundle a copy.
 *
 * Extensions read from each property (brief 21 §1): `x-ui` (`{ widget?, order? }`) picks the control and the field
 * order; `x-classification: 'pii'` masks the value by default (a "Show" toggle reveals it while typing; read-only
 * views use `MaskedValue`). `SchemaForm` is the whole form with an "Advanced" toggle that edits the raw JSON object;
 * `SchemaFields` renders the fields inside a caller's react-hook-form form.
 */
import type { JsonSchemaObject } from '@agentic/contracts';
import { zodResolver } from '@hookform/resolvers/zod';
import { clsx } from 'clsx';
import { type ReactNode, useId, useMemo, useState } from 'react';
import {
  type Control,
  Controller,
  type FieldErrors,
  type Resolver,
  type UseFormRegister,
  useForm,
} from 'react-hook-form';
import { z } from 'zod';
import { Checkbox, describedBy, Field, Input, Select, Textarea } from './fields.tsx';
import { Badge, Button } from './primitives.tsx';

/** The subset of JSON Schema the form renderer understands. */
export interface PropSchema {
  type?: string | string[];
  title?: string;
  description?: string;
  enum?: unknown[];
  format?: string;
  minLength?: number;
  maxLength?: number;
  minimum?: number;
  maximum?: number;
  pattern?: string;
  default?: unknown;
  contentMediaType?: string;
  'x-widget'?: string;
  'x-ui'?: { widget?: string; order?: number };
  'x-classification'?: string;
  properties?: Record<string, PropSchema>;
  required?: string[];
}

export type FieldKind =
  | 'string'
  | 'email'
  | 'textarea'
  | 'number'
  | 'enum'
  | 'radio'
  | 'boolean'
  | 'file'
  | 'date'
  | 'datetime';

/** `x-classification` of a field; absent = `internal` (the platform default). */
export type FieldClass = 'pii' | 'internal' | 'public';

export interface SchemaField {
  name: string;
  kind: FieldKind;
  label: string;
  description?: string;
  required: boolean;
  options?: string[];
  prop: PropSchema;
  /** Location in `inputs` when it differs from `[name]` (fields of a nested object, e.g. `['contact', 'email']`). */
  path?: string[];
  /** `x-classification` (pii values are masked by default). */
  classification: FieldClass;
  /** `x-ui.order` (fields without one keep the schema order after the ordered ones). */
  order?: number;
}

/** Separator of nested-object field names in form values (`contact__email`); RHF would nest on `.`. */
const NESTED = '__';

/** Form values: strings for text/number/enum/file/date (file = file name), booleans for checkboxes. */
export type SchemaValues = Record<string, string | boolean>;

function typeOf(prop: PropSchema): string | undefined {
  return Array.isArray(prop.type) ? prop.type.find((t) => t !== 'null') : prop.type;
}

function widgetOf(prop: PropSchema): string | undefined {
  return prop['x-ui']?.widget ?? prop['x-widget'];
}

function kindOf(prop: PropSchema): FieldKind {
  const type = typeOf(prop);
  const widget = widgetOf(prop);
  if (prop.enum && prop.enum.length > 0) return widget === 'radio' ? 'radio' : 'enum';
  if (type === 'boolean') return 'boolean';
  if (type === 'number' || type === 'integer') return 'number';
  if (prop.format === 'binary' || prop.contentMediaType || widget === 'file') return 'file';
  if (prop.format === 'date-time' || widget === 'datetime') return 'datetime';
  if (prop.format === 'date' || widget === 'date') return 'date';
  if (prop.format === 'textarea' || widget === 'textarea' || (prop.maxLength ?? 0) > 500) return 'textarea';
  if (prop.format === 'email' || widget === 'email') return 'email';
  return 'string';
}

function classOf(prop: PropSchema): FieldClass {
  const c = prop['x-classification'];
  return c === 'pii' || c === 'public' ? c : 'internal';
}

function fieldOf(
  name: string,
  prop: PropSchema,
  required: boolean,
  label: string,
  path?: string[],
): SchemaField {
  const kind = kindOf(prop);
  const order = prop['x-ui']?.order;
  return {
    name,
    kind,
    label,
    ...(prop.description ? { description: prop.description } : {}),
    required,
    ...(kind === 'enum' || kind === 'radio' ? { options: (prop.enum ?? []).map(String) } : {}),
    prop,
    ...(path ? { path } : {}),
    classification: classOf(prop),
    ...(order !== undefined ? { order } : {}),
  };
}

/** Stable sort by `x-ui.order` (unordered fields keep their schema position after the ordered ones). */
function byOrder(fields: SchemaField[]): SchemaField[] {
  return fields
    .map((f, i) => ({ f, i }))
    .sort(
      (a, b) => (a.f.order ?? Number.MAX_SAFE_INTEGER) - (b.f.order ?? Number.MAX_SAFE_INTEGER) || a.i - b.i,
    )
    .map((x) => x.f);
}

/**
 * Flat list of renderable fields from an object schema, ordered by `x-ui.order` (else the `properties` order).
 * Scalar fields of a nested object become fields of their own (`contact__email`, label "Contact · email"); arrays
 * and deeper objects have no control and are left out (`omittedFromSchema` names them).
 */
export function fieldsFromSchema(schema: JsonSchemaObject): SchemaField[] {
  const properties = (schema.properties ?? {}) as Record<string, PropSchema>;
  const required = new Set(Array.isArray(schema.required) ? (schema.required as string[]) : []);
  const fields: SchemaField[] = [];
  for (const [name, prop] of Object.entries(properties)) {
    const type = typeOf(prop);
    if (type === 'array' && kindOf(prop) !== 'file') continue;
    if (type === 'object' || prop.properties) {
      const childRequired = new Set(prop.required ?? []);
      for (const [child, childProp] of Object.entries(prop.properties ?? {})) {
        const childType = typeOf(childProp);
        if (childType === 'object' || childType === 'array' || childProp.properties) continue;
        fields.push(
          fieldOf(
            `${name}${NESTED}${child}`,
            childProp,
            required.has(name) && childRequired.has(child),
            `${prop.title ?? name} · ${childProp.title ?? child}`,
            [name, child],
          ),
        );
      }
      continue;
    }
    fields.push(fieldOf(name, prop, required.has(name), prop.title ?? name));
  }
  return byOrder(fields);
}

/**
 * Schema properties the form has no control for (arrays other than files, objects nested more than one level), so
 * the caller can say so before submission instead of dropping them silently. Labels follow `fieldsFromSchema`.
 */
export function omittedFromSchema(schema: JsonSchemaObject): string[] {
  const properties = (schema.properties ?? {}) as Record<string, PropSchema>;
  const out: string[] = [];
  for (const [name, prop] of Object.entries(properties)) {
    const type = typeOf(prop);
    if (type === 'array' && kindOf(prop) !== 'file') {
      out.push(`${prop.title ?? name} (list)`);
      continue;
    }
    if (type === 'object' || prop.properties) {
      for (const [child, childProp] of Object.entries(prop.properties ?? {})) {
        const childType = typeOf(childProp);
        if (childType === 'object' || childType === 'array' || childProp.properties)
          out.push(
            `${prop.title ?? name} · ${childProp.title ?? child}${childType === 'array' ? ' (list)' : ''}`,
          );
      }
    }
  }
  return out;
}

/** Initial form values from schema defaults. */
export function defaultsFromSchema(fields: SchemaField[]): SchemaValues {
  const out: SchemaValues = {};
  for (const f of fields) {
    if (f.kind === 'boolean') out[f.name] = f.prop.default === true;
    else out[f.name] = f.prop.default === undefined ? '' : String(f.prop.default);
  }
  return out;
}

/** `datetime-local` value (`2026-11-01T09:30`) of an ISO time, in the browser's zone. */
function localInputOf(iso: string): string {
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return '';
  const pad = (n: number) => String(n).padStart(2, '0');
  return `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}T${pad(d.getHours())}:${pad(d.getMinutes())}`;
}

/** Form values from an object (the advanced JSON view back to the fields; unknown keys are ignored). */
export function valuesFromObject(fields: SchemaField[], value: Record<string, unknown>): SchemaValues {
  const out = defaultsFromSchema(fields);
  for (const f of fields) {
    const [head, ...rest] = f.path ?? [f.name];
    if (head === undefined) continue;
    let v: unknown = value[head];
    if (rest.length > 0)
      v = typeof v === 'object' && v !== null ? (v as Record<string, unknown>)[rest.join('.')] : undefined;
    if (v === undefined || v === null) continue;
    if (f.kind === 'boolean') out[f.name] = v === true;
    else if (f.kind === 'datetime' && typeof v === 'string') out[f.name] = localInputOf(v);
    else out[f.name] = typeof v === 'object' ? JSON.stringify(v) : String(v);
  }
  return out;
}

/** Zod validator for the form values of these fields (messages are user-facing). */
export function zodFromFields(fields: SchemaField[]): z.ZodType<SchemaValues> {
  const shape: Record<string, z.ZodType> = {};
  for (const f of fields) {
    if (f.kind === 'boolean') {
      shape[f.name] = z.boolean();
      continue;
    }
    let s = z.string().trim();
    const p = f.prop;
    const checks: ((v: string) => string | null)[] = [];
    if (f.kind === 'number') {
      checks.push((v) => (Number.isNaN(Number(v)) ? 'Enter a number' : null));
      if (typeOf(p) === 'integer')
        checks.push((v) => (Number.isInteger(Number(v)) ? null : 'Enter a whole number'));
      if (p.minimum !== undefined)
        checks.push((v) => (Number(v) < (p.minimum ?? 0) ? `Minimum ${p.minimum}` : null));
      if (p.maximum !== undefined)
        checks.push((v) => (Number(v) > (p.maximum ?? 0) ? `Maximum ${p.maximum}` : null));
    }
    if (f.kind === 'email')
      checks.push((v) => (z.email().safeParse(v).success ? null : 'Enter a valid email'));
    if (f.kind === 'enum' || f.kind === 'radio')
      checks.push((v) => (f.options?.includes(v) ? null : 'Choose an option'));
    if (f.kind === 'datetime' || f.kind === 'date')
      checks.push((v) => (Number.isNaN(Date.parse(v)) ? 'Enter a valid date' : null));
    if (p.minLength !== undefined)
      checks.push((v) => (v.length < (p.minLength ?? 0) ? `At least ${p.minLength} characters` : null));
    if (p.maxLength !== undefined) s = s.max(p.maxLength, `At most ${p.maxLength} characters`);
    // Date fields carry the ISO pattern of the wire format; the control holds a local value (converted on submit).
    if (p.pattern && f.kind !== 'datetime' && f.kind !== 'date') {
      const re = new RegExp(p.pattern);
      checks.push((v) => (re.test(v) ? null : 'Invalid format'));
    }
    shape[f.name] = s.superRefine((v, ctx) => {
      if (v === '') {
        if (f.required) ctx.addIssue({ code: 'custom', message: 'Required' });
        return;
      }
      for (const check of checks) {
        const message = check(v);
        if (message) {
          ctx.addIssue({ code: 'custom', message });
          return;
        }
      }
    });
  }
  return z.object(shape) as unknown as z.ZodType<SchemaValues>;
}

/** Converts form values into the object the schema describes (numbers parsed, dates ISO, empty values omitted). */
export function toInputs(
  fields: SchemaField[],
  values: SchemaValues,
): { inputs: Record<string, unknown>; files: string[] } {
  const inputs: Record<string, unknown> = {};
  const files: string[] = [];
  const put = (f: SchemaField, value: unknown) => {
    const [head, ...rest] = f.path ?? [f.name];
    if (head === undefined) return;
    if (rest.length === 0) {
      inputs[head] = value;
      return;
    }
    const existing = inputs[head];
    const parent: Record<string, unknown> =
      typeof existing === 'object' && existing !== null ? (existing as Record<string, unknown>) : {};
    parent[rest.join('.')] = value;
    inputs[head] = parent;
  };
  for (const f of fields) {
    const v = values[f.name];
    if (v === undefined || v === '') continue;
    if (f.kind === 'boolean') put(f, v === true);
    else if (f.kind === 'number') put(f, Number(v));
    else if (f.kind === 'datetime' && typeof v === 'string') put(f, new Date(v).toISOString());
    else {
      put(f, v);
      if (f.kind === 'file' && typeof v === 'string') files.push(`upload:${v}`);
    }
  }
  return { inputs, files };
}

export interface SchemaFieldsProps {
  fields: SchemaField[];
  /** Prefix in the form values, e.g. `inputs`. */
  prefix: string;
  /** Prefix of the control ids (default `prefix`); set it when one page holds two forms with the same prefix. */
  idPrefix?: string;
  register: UseFormRegister<Record<string, unknown>>;
  control: Control<Record<string, unknown>>;
  errors: FieldErrors<Record<string, unknown>> | undefined;
  /**
   * Receives the chosen `File` of a file field (undefined when cleared). When set, the caller uploads the file after
   * the case starts and file fields say so; without it, file fields say that only the name is sent.
   */
  onFileChange?: (fieldName: string, file: File | undefined) => void;
}

/** Hint of a file field: what actually happens to the file, so choosing one never implies an upload that is not made. */
export function fileFieldNote(uploads: boolean): string {
  return uploads
    ? 'The file is uploaded right after the case starts; you will see whether each upload worked.'
    : 'Attachment name only, the file is not uploaded by this form.';
}

/** "Personal data" marker of a pii field. */
export function PiiBadge() {
  return (
    <Badge tone="warn" className="ml-2 align-middle" data-testid="pii-badge">
      Personal data
    </Badge>
  );
}

/** A text input whose value is hidden until the user shows it (pii fields). */
function MaskedInput({
  id,
  inputProps,
}: {
  id: string;
  inputProps: Omit<React.ComponentProps<typeof Input>, 'type'>;
}) {
  const [shown, setShown] = useState(false);
  return (
    <div className="flex items-center gap-2">
      <Input
        id={id}
        type={shown ? 'text' : 'password'}
        autoComplete="off"
        data-masked={!shown || undefined}
        className="min-w-0 flex-1"
        {...inputProps}
      />
      <Button
        size="sm"
        variant="ghost"
        aria-pressed={shown}
        aria-controls={id}
        onClick={() => setShown(!shown)}
      >
        {shown ? 'Hide' : 'Show'}
      </Button>
    </div>
  );
}

/** Renders controls for schema fields inside a react-hook-form form. */
export function SchemaFields({
  fields,
  prefix,
  idPrefix,
  register,
  control,
  errors,
  onFileChange,
}: SchemaFieldsProps) {
  return (
    <>
      {fields.map((f) => {
        const id = `${idPrefix ?? prefix}-${f.name}`;
        const name = `${prefix}.${f.name}`;
        const error = (errors?.[f.name] as { message?: string } | undefined)?.message;
        const pii = f.classification === 'pii';
        const label = (
          <>
            {f.label}
            {pii && <PiiBadge />}
          </>
        );
        const hint =
          f.kind === 'file' ? (
            <>
              {f.description && <span className="block">{f.description}</span>}
              <span className="block">{fileFieldNote(!!onFileChange)}</span>
            </>
          ) : pii ? (
            <>
              {f.description && <span className="block">{f.description}</span>}
              <span className="block">Personal data: hidden while you type; choose Show to check it.</span>
            </>
          ) : (
            f.description
          );
        const aria = {
          'aria-invalid': !!error,
          'aria-describedby': describedBy(id, !!error, !!hint),
        };
        if (f.kind === 'boolean') {
          return (
            <div key={f.name} className="flex items-center gap-2" data-field={f.name}>
              <Checkbox id={id} {...register(name)} />
              <label htmlFor={id} className="text-[13px] text-ink">
                {label}
              </label>
            </div>
          );
        }
        if (f.kind === 'radio') {
          return (
            <fieldset
              key={f.name}
              className="flex flex-col gap-1.5"
              data-field={f.name}
              aria-describedby={aria['aria-describedby']}
            >
              <legend className="text-[13px] font-medium text-ink-2">
                {label}
                {f.required && <span className="sr-only"> (required)</span>}
              </legend>
              <div className="flex flex-wrap gap-x-4 gap-y-1">
                {f.options?.map((o) => (
                  <label key={o} className="inline-flex items-center gap-1.5 text-[13.5px] text-ink">
                    <input type="radio" value={o} className="accent-[var(--wb-accent)]" {...register(name)} />
                    {o}
                  </label>
                ))}
              </div>
              {hint && !error && (
                <p id={`${id}-hint`} className="text-[13px] text-ink-3">
                  {hint}
                </p>
              )}
              {error && (
                <p id={`${id}-error`} role="alert" className="text-[13px] font-medium text-bad">
                  {error}
                </p>
              )}
            </fieldset>
          );
        }
        return (
          <Field key={f.name} id={id} label={label} hint={hint} error={error} required={f.required}>
            {f.kind === 'enum' ? (
              <Select id={id} {...aria} {...register(name)}>
                <option value="">Choose…</option>
                {f.options?.map((o) => (
                  <option key={o} value={o}>
                    {o}
                  </option>
                ))}
              </Select>
            ) : f.kind === 'textarea' ? (
              <Textarea id={id} rows={3} {...aria} {...register(name)} />
            ) : f.kind === 'file' ? (
              <Controller
                control={control}
                name={name}
                render={({ field }) => (
                  <>
                    <Input
                      id={id}
                      type="file"
                      className="h-auto py-1.5 file:mr-3 file:rounded file:border-0 file:bg-surface-2 file:px-2 file:py-1 file:text-[13px] file:text-ink"
                      {...aria}
                      onBlur={field.onBlur}
                      onChange={(e) => {
                        const file = e.target.files?.[0];
                        field.onChange(file?.name ?? '');
                        onFileChange?.(f.name, file);
                      }}
                    />
                    {typeof field.value === 'string' && field.value !== '' && (
                      <span className="text-[13px] text-ink-2">
                        Selected: <span className="font-medium text-ink">{field.value}</span>
                      </span>
                    )}
                  </>
                )}
              />
            ) : pii && (f.kind === 'string' || f.kind === 'email') ? (
              <MaskedInput id={id} inputProps={{ ...aria, ...register(name) }} />
            ) : (
              <Input
                id={id}
                type={
                  f.kind === 'number'
                    ? 'number'
                    : f.kind === 'email'
                      ? 'email'
                      : f.kind === 'datetime'
                        ? 'datetime-local'
                        : f.kind === 'date'
                          ? 'date'
                          : 'text'
                }
                inputMode={f.kind === 'number' ? 'decimal' : undefined}
                {...aria}
                {...register(name)}
              />
            )}
          </Field>
        );
      })}
    </>
  );
}

/** Masks a value for display: the last four characters stay visible (`•••• 4411`). */
export function maskValue(value: string): string {
  if (value.length <= 4) return '••••';
  return `•••• ${value.slice(-4)}`;
}

/**
 * A read-only value; masked when `pii` until the viewer chooses "Show". `revealable: false` (added by 26c) keeps it
 * masked with no "Show" (a viewer who may not act on the record).
 */
export function MaskedValue({
  value,
  pii,
  label,
  revealable = true,
}: {
  value: string;
  pii: boolean;
  label?: string;
  revealable?: boolean;
}) {
  const [shown, setShown] = useState(false);
  if (!pii) return <span>{value}</span>;
  if (!revealable) {
    return (
      <span className="font-mono text-[12.5px]" data-masked>
        {maskValue(value)}
      </span>
    );
  }
  return (
    <span className="inline-flex items-center gap-1.5" data-masked={!shown || undefined}>
      <span className="font-mono text-[12.5px]">{shown ? value : maskValue(value)}</span>
      <button
        type="button"
        className="text-[12px] text-accent-ink underline"
        aria-pressed={shown}
        aria-label={`${shown ? 'Hide' : 'Show'}${label ? ` ${label}` : ''}`}
        onClick={() => setShown(!shown)}
      >
        {shown ? 'Hide' : 'Show'}
      </button>
    </span>
  );
}

/** Rows of an object for display against its schema (labels, order and pii masking from the schema). */
export function SchemaValueList({
  schema,
  value,
  className,
  revealable = true,
}: {
  schema: JsonSchemaObject | undefined;
  value: Record<string, unknown>;
  className?: string;
  /** Added by 26c: false keeps pii values masked with no "Show". */
  revealable?: boolean;
}) {
  const fields = schema ? fieldsFromSchema(schema) : [];
  const known = new Set(fields.map((f) => (f.path ?? [f.name])[0]));
  const rows: { key: string; label: string; text: string; pii: boolean }[] = [];
  for (const f of fields) {
    const [head, ...rest] = f.path ?? [f.name];
    if (head === undefined) continue;
    let v: unknown = value[head];
    if (rest.length > 0)
      v = typeof v === 'object' && v !== null ? (v as Record<string, unknown>)[rest.join('.')] : undefined;
    if (v === undefined) continue;
    rows.push({
      key: f.name,
      label: f.label,
      text: typeof v === 'string' ? v : JSON.stringify(v),
      pii: f.classification === 'pii',
    });
  }
  for (const [k, v] of Object.entries(value)) {
    if (known.has(k)) continue;
    rows.push({ key: k, label: k, text: typeof v === 'string' ? v : JSON.stringify(v), pii: false });
  }
  if (rows.length === 0) return null;
  return (
    <dl className={clsx('grid grid-cols-[minmax(8rem,auto)_1fr] gap-x-4 gap-y-1.5 text-[13px]', className)}>
      {rows.map((r) => (
        <div key={r.key} className="contents" data-field={r.key}>
          <dt className="text-ink-3">{r.label}</dt>
          <dd className="min-w-0 break-words text-ink">
            <MaskedValue value={r.text} pii={r.pii} label={r.label} revealable={revealable} />
          </dd>
        </div>
      ))}
    </dl>
  );
}

export interface SchemaFormProps {
  /** The served JSON Schema (object). */
  schema: JsonSchemaObject;
  /** Called with the object the schema describes; resolve when the server accepted it (reject shows nothing here). */
  onSubmit: (value: Record<string, unknown>) => Promise<unknown> | undefined;
  submitLabel: string;
  /** Accessible name of the form. */
  label: string;
  /** Initial value (e.g. a draft). */
  initial?: Record<string, unknown>;
  busy?: boolean;
  /** Rendered above the buttons (errors from the server). */
  footer?: ReactNode;
  /** Extra buttons left of the submit button (e.g. "Not now"). */
  secondary?: ReactNode;
  /** Shows the "Advanced: raw JSON" toggle (default true). */
  advanced?: boolean;
}

/** Parses the raw JSON of the advanced view; an error message for anything but a JSON object. */
export function parseJsonObject(
  text: string,
): { ok: true; value: Record<string, unknown> } | { ok: false; error: string } {
  let value: unknown;
  try {
    value = JSON.parse(text.trim() === '' ? '{}' : text);
  } catch {
    return { ok: false, error: 'Enter a JSON object, for example {"field": "value"}.' };
  }
  if (typeof value !== 'object' || value === null || Array.isArray(value)) {
    return { ok: false, error: 'The value must be a JSON object.' };
  }
  return { ok: true, value: value as Record<string, unknown> };
}

/**
 * A whole form from a served schema: the fields (ordered by `x-ui`, pii masked), a note naming properties without a
 * control, and an "Advanced" toggle that edits the raw JSON object (switching back carries the values over). The
 * server validates again; this only catches what the schema states.
 */
export function SchemaForm({
  schema,
  onSubmit,
  submitLabel,
  label,
  initial,
  busy,
  footer,
  secondary,
  advanced = true,
}: SchemaFormProps) {
  const uid = useId();
  const fields = useMemo(() => fieldsFromSchema(schema), [schema]);
  const omitted = useMemo(() => omittedFromSchema(schema), [schema]);
  const piiLabels = useMemo(
    () => fields.filter((f) => f.classification === 'pii').map((f) => f.label),
    [fields],
  );
  const validator = useMemo(() => z.object({ v: zodFromFields(fields) }), [fields]);
  const form = useForm<{ v: SchemaValues }>({
    resolver: zodResolver(validator) as unknown as Resolver<{ v: SchemaValues }>,
    defaultValues: { v: initial ? valuesFromObject(fields, initial) : defaultsFromSchema(fields) },
  });
  const [raw, setRaw] = useState<string | undefined>(undefined);
  const parsed = raw === undefined ? undefined : parseJsonObject(raw);
  const toggleRaw = () => {
    if (raw === undefined) {
      const { inputs } = toInputs(fields, form.getValues().v);
      setRaw(JSON.stringify(inputs, null, 2));
    } else {
      if (parsed?.ok) form.reset({ v: valuesFromObject(fields, parsed.value) });
      setRaw(undefined);
    }
  };
  const submit = form.handleSubmit(async ({ v }) => {
    await onSubmit(toInputs(fields, v).inputs);
  });
  return (
    <form
      noValidate
      aria-label={label}
      className="flex flex-col gap-4"
      data-testid="schema-form"
      onSubmit={(e) => {
        if (raw !== undefined) {
          e.preventDefault();
          if (parsed?.ok) void onSubmit(parsed.value);
          return;
        }
        void submit(e);
      }}
    >
      {raw === undefined ? (
        <div className="grid grid-cols-1 gap-4">
          <SchemaFields
            fields={fields}
            prefix="v"
            idPrefix={`sf${uid.replace(/[^a-zA-Z0-9]/g, '')}`}
            register={form.register as never}
            control={form.control as never}
            errors={form.formState.errors.v as never}
          />
          {fields.length === 0 && (
            <p className="text-[13px] text-ink-2">
              The schema declares no fields; use Advanced to send a JSON object.
            </p>
          )}
          {omitted.length > 0 && (
            <p className="text-[13px] text-ink-2" data-testid="schema-omitted">
              This form has no field for: {omitted.join(', ')}. Use Advanced to enter them as JSON.
            </p>
          )}
        </div>
      ) : (
        <>
          {piiLabels.length > 0 && (
            <p
              className="rounded-lg bg-warn-soft px-3 py-2 text-[13px] text-warn-ink"
              role="note"
              data-testid="schema-raw-pii"
            >
              Contains personal data: the raw JSON shows {piiLabels.join(', ')} unmasked.
            </p>
          )}
          <Field
            id={`${uid}-raw`}
            label="Raw JSON object"
            error={parsed && !parsed.ok ? parsed.error : undefined}
            hint="The server checks the object against the same schema."
          >
            <Textarea
              id={`${uid}-raw`}
              data-testid="schema-raw-json"
              className="font-mono text-[13px]"
              rows={10}
              value={raw}
              onChange={(e) => setRaw(e.target.value)}
            />
          </Field>
        </>
      )}
      {footer}
      <div className="flex flex-wrap items-center justify-between gap-3 border-t border-line pt-4">
        {advanced ? (
          <Button
            size="sm"
            variant="ghost"
            aria-pressed={raw !== undefined}
            data-testid="schema-advanced"
            disabled={raw !== undefined && parsed !== undefined && !parsed.ok}
            onClick={toggleRaw}
          >
            {raw === undefined ? 'Advanced: edit raw JSON' : 'Back to the form'}
          </Button>
        ) : (
          <span />
        )}
        <div className="flex flex-wrap gap-2">
          {secondary}
          <Button type="submit" variant="primary" loading={busy} disabled={raw !== undefined && !parsed?.ok}>
            {submitLabel}
          </Button>
        </div>
      </div>
    </form>
  );
}
